import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useCallback } from 'react';
import { useLocalizeMessage } from 'libs.nucleus';
import { useReauthentication } from 'libs.react_internal';
import { LogEvents } from '../../constants/log_events.constants';
import { signOffPIOversight } from '../../helpers/pi_oversight.helpers';
import { useCurrentEnvironment } from '../use_current_environment';
import { useLumosEventLogger } from '../use_lumos_event_logger';
import { useOrgContext } from '../use_org_context';
import { usePIOversight } from '../use_pi_oversight';
import { useSiteContext } from '../use_site_context';
// Helper to get IAM token from cookie
const getIAMTokenFromCookie = () => {
    try {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === 'md_idtoken') {
                return decodeURIComponent(value);
            }
        }
        return null;
    }
    catch {
        return null;
    }
};
export const usePIOversightSignoff = (participantId, options) => {
    const { environment } = useCurrentEnvironment();
    const queryClient = useQueryClient();
    const site = useSiteContext();
    const org = useOrgContext();
    const translate = useLocalizeMessage();
    const { logLumosEvent } = useLumosEventLogger();
    // Get review bundle and summary (automatically fetched by usePIOversight)
    const { reviewBundle, summary } = usePIOversight({ participantId });
    const [isProcessingSignoff, setIsProcessingSignoff] = useState(false);
    const logEvent = (event, payload = {}) => {
        const payloadToSend = {
            participantId: participantId ?? reviewBundle?.participantId,
            summaryGenerated: !!summary?.summary,
            ...payload,
        };
        logLumosEvent(event, payloadToSend);
    };
    // Use reauth hook for handling password/SSO authentication
    const { reauthenticate, isReauthenticating, showPasswordModal, closePasswordModal, handlePasswordSubmit, passwordError, } = useReauthentication({
        onSuccess: (token, printedName) => {
            processSignoff(token, printedName || options?.defaultPrintedName || '');
        },
        onError: (error) => {
            options?.onError?.(error);
        },
        txn: reviewBundle?.txn,
        defaultPrintedName: options?.defaultPrintedName,
    });
    const processSignoff = useCallback(async (iamToken, printedName) => {
        setIsProcessingSignoff(true);
        try {
            if (!environment || !site || !org) {
                throw new Error('Environment not available');
            }
            if (!reviewBundle?.serverHash) {
                throw new Error('Review data not available. Please try again.');
            }
            const iamEndpoint = process.env.IAM_SERVICE_ENDPOINT;
            if (!iamEndpoint) {
                throw new Error('IAM endpoint not configured');
            }
            await signOffPIOversight({
                environment,
                payload: {
                    participantId: reviewBundle.participantId,
                    siteId: site._id,
                    studyId: org._id,
                    signedDataHash: reviewBundle.serverHash,
                    iamEndpoint,
                    iamToken,
                    aiSummary: summary?.summary || translate('info.pi_oversight.no_summary_generated'),
                    printedName,
                },
                onError: (error) => {
                    throw error;
                },
            });
            // clear summary data from cache (sync)
            queryClient.removeQueries({ queryKey: ['summaryData', reviewBundle.participantId] });
            // trigger refetchs for related queries (async)
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['piOversightList'], refetchType: 'active' }),
                queryClient.invalidateQueries({ queryKey: ['reviewBundleData', reviewBundle.participantId] }),
                queryClient.invalidateQueries({ queryKey: ['participantDetails', reviewBundle.participantId] }),
            ]);
            setIsProcessingSignoff(false);
            logEvent(LogEvents.PI_OVERSIGHT_SIGN_OFF_SUCCESS);
            options?.onSuccess?.();
        }
        catch (error) {
            // Map backend errors to user-friendly messages
            let errorMessage = 'Sign-off failed. Please try again.';
            if (error?.response?.data?.reason) {
                const { reason } = error.response.data;
                if (reason.includes('hash mismatch') || reason.includes('data has changed')) {
                    errorMessage = 'The participant data has changed. Please refresh and review the updated data.';
                }
                else if (reason.includes('expired') || reason.includes('inactive')) {
                    errorMessage = 'Your authentication has expired. Please try again.';
                }
                else if (reason.includes('stale') || reason.includes('minutes old')) {
                    errorMessage = 'Your authentication took too long. Please try again.';
                }
                else if (reason.includes('Access denied') || reason.includes('not authorized')) {
                    errorMessage = 'You are not authorized to sign off. Only Principal Investigators can sign off.';
                }
                else {
                    errorMessage = reason;
                }
            }
            else if (error?.message) {
                errorMessage = error.message;
            }
            setIsProcessingSignoff(false);
            logEvent(LogEvents.PI_OVERSIGHT_SIGN_OFF_FAILED, {
                error: errorMessage,
            });
            options?.onError?.(errorMessage);
        }
    }, [environment, queryClient, reviewBundle, summary, site, org, options, translate]);
    // Auto-process sign-off when page loads after SSO redirect
    useEffect(() => {
        const iamToken = getIAMTokenFromCookie();
        const iamEndpoint = process.env.IAM_SERVICE_ENDPOINT;
        const { referrer } = document;
        // Check if user just came from IAM (via referrer)
        const isFromIAM = referrer && iamEndpoint && (referrer.includes(iamEndpoint) || referrer.includes('/login'));
        // Only process if user came from IAM/login and we have token + required data
        if (isFromIAM && iamToken && reviewBundle?.serverHash) {
            processSignoff(iamToken, options?.defaultPrintedName || '');
        }
    }, [reviewBundle, summary, processSignoff, options?.defaultPrintedName]);
    return {
        isProcessingSignoff: isProcessingSignoff || isReauthenticating,
        triggerSignOff: reauthenticate,
        showPasswordModal,
        closePasswordModal,
        handlePasswordSubmit,
        passwordError,
    };
};
